import React, { JSX, useState } from "react";
import { Icon } from "components/icons/icon";
import { IconSizes } from "components/icons/constants/icon-sizes";
import { Icons } from "components/icons/constants/icons";
import { StringUtils } from "utilities/string-utils";
import "./accordion.scss";
import { clsx } from "clsx";

// -------------------------------------------------------------------------------------------------
// #region Enums
// -------------------------------------------------------------------------------------------------

export enum AccordionStyle {
    Card = "-card",
    Default = "",
    Inverse = "-inverse",
}

// #endregion Enums

// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

type ChevronIcon = Icons.ChevronUp | Icons.ChevronRight | Icons.ChevronDown | Icons.ChevronLeft;

export interface AccordionProps {
    ariaLabelledBy?: string;
    cssClassName?: string;
    chevronLeft?: boolean;
    closedChevron?: ChevronIcon;
    number?: number;
    openChevron?: ChevronIcon;
    rightContent?: React.ReactNode;
    style?: AccordionStyle;
    subTitle?: string;
    text?: string;
    title?: string;
}

export interface AccordionChevronProps {
    closedChevron?: ChevronIcon;
    openChevron?: ChevronIcon;
    isActive: boolean;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// -------------------------------------------------------------------------------------------------
const ANGLE: Record<ChevronIcon, number> = {
    [Icons.ChevronUp]: 0,
    [Icons.ChevronRight]: 90,
    [Icons.ChevronDown]: 180,
    [Icons.ChevronLeft]: 270,
};

const CSS_CLASS_NAME: string = "accordion";

const ROTATE: Record<number, string> = {
    0: "rotate-0",
    90: "rotate-90",
    180: "rotate-180",
    [-90]: "-rotate-90",
};

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const Accordion: React.FC<React.PropsWithChildren<AccordionProps>> = (
    props: React.PropsWithChildren<AccordionProps>
) => {
    const [isActive, setIsActive] = useState(false);
    const [buttonStyle, setButtonStyle] = useState("__button");

    const openAccordion = () => {
        setIsActive(!isActive);
        if (isActive) {
            setButtonStyle("__button");
        } else {
            setButtonStyle("__button-open");
        }
    };

    const cssClassNames: string[] = [CSS_CLASS_NAME];

    if (StringUtils.hasValue(props.cssClassName)) {
        cssClassNames.push(props.cssClassName);
    }

    if (props.style) {
        cssClassNames.push(props.style);
    }

    return (
        <div className={cssClassNames.join(" ")} title={props.ariaLabelledBy}>
            <div className={`${CSS_CLASS_NAME}__item`}>
                <h2 className={`${CSS_CLASS_NAME}__heading`}>
                    <button
                        type="button"
                        onClick={() => openAccordion()}
                        className={clsx(
                            `${CSS_CLASS_NAME}${buttonStyle}`,
                            props.chevronLeft ? "justify-normal" : "justify-between"
                        )}>
                        {props.chevronLeft && (
                            <div className="mr-2">
                                <AccordionChevron
                                    isActive={isActive}
                                    closedChevron={props.closedChevron}
                                    openChevron={props.openChevron}
                                />
                            </div>
                        )}
                        {
                            // if
                            StringUtils.hasValue(props.title) && (
                                <span className={`${CSS_CLASS_NAME}__title`}>
                                    {props.number && (
                                        <span className="-number">
                                            {props.number}
                                            {". "}
                                        </span>
                                    )}
                                    {props.title}
                                </span>
                            )
                        }
                        <div className={`${CSS_CLASS_NAME}__button-right`}>
                            {props.rightContent}
                            <div className={`${CSS_CLASS_NAME}__subtitle`}>
                                {props.subTitle?.toUpperCase()}
                            </div>
                            {!props.chevronLeft && (
                                <AccordionChevron
                                    isActive={isActive}
                                    closedChevron={props.closedChevron}
                                    openChevron={props.openChevron}
                                />
                            )}
                        </div>
                    </button>
                </h2>
                {isActive && (
                    <div className={`${CSS_CLASS_NAME}__details`}>
                        {props.children}
                    </div>
                )}
            </div>
        </div>
    );
};

const openRotation = (closed: ChevronIcon, open: ChevronIcon): string => {
    const delta = (ANGLE[open] - ANGLE[closed] + 360) % 360;
    return ROTATE[delta === 270 ? -90 : delta];
};

const AccordionChevron = ({
    isActive,
    closedChevron = Icons.ChevronDown,
    openChevron = Icons.ChevronUp,
}: AccordionChevronProps): JSX.Element => {
    return (
        <div className={`${CSS_CLASS_NAME}__chevron`}>
            <Icon
                cssClassName={clsx(
                    "transition-transform duration-200 ease-out",
                    isActive ? openRotation(closedChevron, openChevron) : ROTATE[0]
                )}
                type={closedChevron}
                size={IconSizes.Large}
            />
        </div>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { Accordion };

// #endregion Exports
